import type { ApiError, ApiFieldError } from '../api/types';
import type { RegularLangKey, RegularLangKeyWithVariables } from '../types/language';

import { DEBUG } from '../config';
import { getTranslationFn, type RegularLangFnParameters } from './localization';

const READABLE_ERROR_MESSAGES: Record<string, RegularLangKey | RegularLangKeyWithVariables> = {
  CHAT_RESTRICTED: 'ApiErrChatRestricted',
  CHAT_SEND_POLL_FORBIDDEN: 'ApiErrChatSendPollForbidden',
  CHAT_SEND_STICKERS_FORBIDDEN: 'ApiErrChatSendStickersForbidden',
  CHAT_SEND_GIFS_FORBIDDEN: 'ApiErrChatSendGifsForbidden',
  CHAT_SEND_MEDIA_FORBIDDEN: 'ApiErrChatSendMediaForbidden',
  VOICE_MESSAGES_FORBIDDEN: 'ApiErrVoiceMessagesForbidden',
  TRAINING_RECORDING_INVALID: 'TrainingUploadInvalid',
  CHAT_LINK_EXISTS: 'ApiErrChatLinkExists',
  SLOWMODE_WAIT_X: 'ApiErrSlowmodeWait',
  USER_BANNED_IN_CHANNEL: 'ApiErrUserBannedInChannel',
  USER_IS_BLOCKED: 'ApiErrUserIsBlocked',
  YOU_BLOCKED_USER: 'ApiErrYouBlockedUser',
  IMAGE_PROCESS_FAILED: 'ApiErrImageProcessFailed',
  MEDIA_EMPTY: 'ApiErrMediaEmpty',
  MEDIA_GROUPED_INVALID: 'ApiErrMediaGroupedInvalid',
  MEDIA_NEW_INVALID: 'ApiErrMediaNewInvalid',
  MESSAGE_NOT_MODIFIED: 'ApiErrMessageNotModified',
  MEDIA_INVALID: 'ApiErrMediaInvalid',
  PASSWORD_HASH_INVALID: 'ApiErrPasswordHashInvalid',
  PHOTO_EXT_INVALID: 'ApiErrPhotoExtInvalid',
  PHOTO_INVALID_DIMENSIONS: 'ApiErrPhotoInvalidDimensions',
  PHOTO_SAVE_FILE_INVALID: 'ApiErrPhotoSaveFileInvalid',
  MESSAGE_DELETE_FORBIDDEN: 'ApiErrMessageDeleteForbidden',
  MESSAGE_POLL_CLOSED: 'ApiErrMessagePollClosed',
  MESSAGE_EDIT_TIME_EXPIRED: 'ApiErrMessageEditTimeExpired',
  PINNED_DIALOGS_TOO_MUCH: 'ApiErrPinnedDialogsTooMuch',
  CHANNEL_PRIVATE: 'ApiErrChannelPrivate',
  MEDIA_CAPTION_TOO_LONG: 'ApiErrMediaCaptionTooLong',
  ADDRESS_STREET_LINE1_INVALID: 'ApiErrAddressStreetInvalid',
  ADDRESS_STREET_LINE2_INVALID: 'ApiErrAddressStreetInvalid',
  ADDRESS_CITY_INVALID: 'ApiErrAddressCityInvalid',
  ADDRESS_COUNTRY_INVALID: 'ApiErrAddressCountryInvalid',
  ADDRESS_POSTCODE_INVALID: 'ApiErrAddressPostcodeInvalid',
  ADDRESS_STATE_INVALID: 'ApiErrAddressStateInvalid',
  REQ_INFO_NAME_INVALID: 'ApiErrReqInfoNameInvalid',
  REQ_INFO_PHONE_INVALID: 'ApiErrReqInfoPhoneInvalid',
  REQ_INFO_EMAIL_INVALID: 'ApiErrReqInfoEmailInvalid',
  // TODO Bring back after fixing the weird bug
  // CHANNEL_INVALID: 'An error occurred. Please try again later',
  LINK_NOT_MODIFIED: 'ApiErrLinkNotModified',
  MESSAGE_TOO_LONG: 'ApiErrMessageTooLong',

  // Non-API errors
  SERVICE_WORKER_DISABLED: 'ApiErrServiceWorkerDisabled',
  MESSAGE_TOO_LONG_PLEASE_REMOVE_CHARACTERS: 'ApiErrMessageTooLongRemoveChars',
  FRESH_RESET_AUTHORISATION_FORBIDDEN: 'ApiErrFreshResetAuthForbidden',

  BOTS_TOO_MUCH: 'ApiErrBotsTooMuch',
  BOT_GROUPS_BLOCKED: 'ApiErrBotGroupsBlocked',
  USERS_TOO_MUCH: 'ApiErrUsersTooMuch',
  USER_CHANNELS_TOO_MUCH: 'ApiErrUserChannelsTooMuch',
  USER_KICKED: 'ApiErrUserKicked',
  USER_NOT_MUTUAL_CONTACT: 'ApiErrUserNotMutualContact',
  USER_PRIVACY_RESTRICTED: 'ApiErrUserPrivacyRestricted',
  INVITE_HASH_EMPTY: 'ApiErrInviteHashEmpty',
  INVITE_HASH_EXPIRED: 'ApiErrInviteHashExpired',
  INVITE_HASH_INVALID: 'ApiErrInviteHashInvalid',
  CHANNELS_TOO_MUCH: 'ApiErrChannelsTooMuch',
  USER_ALREADY_PARTICIPANT: 'ApiErrUserAlreadyParticipant',
  SCHEDULE_DATE_INVALID: 'ApiErrScheduleDateInvalid',
  WALLPAPER_DIMENSIONS_INVALID: 'ApiErrWallpaperDimensionsInvalid',
  ADMINS_TOO_MUCH: 'ApiErrAdminsTooMuch',
  ADMIN_RANK_EMOJI_NOT_ALLOWED: 'ApiErrAdminRankEmojiNotAllowed',
  ADMIN_RANK_INVALID: 'ApiErrAdminRankInvalid',
  FRESH_CHANGE_ADMINS_FORBIDDEN: 'ApiErrFreshChangeAdminsForbidden',
  SESSION_TOO_FRESH: 'ApiErrSessionFresh',
  SESSION_IS_FRESH: 'ApiErrSessionFresh',
  INPUT_USER_DEACTIVATED: 'ApiErrInputUserDeactivated',
  BOT_PRECHECKOUT_TIMEOUT: 'ApiErrBotPrecheckoutTimeout',
  PROVIDER_ACCOUNT_TIMEOUT: 'ApiErrProviderAccountTimeout',

  STARGIFT_CONVERT_TOO_OLD: 'ApiErrStargiftConvertTooOld',
  SUBSCRIPTION_ALREADY_ACTIVE: 'ApiErrSubscriptionAlreadyActive',

  PEERS_LIST_EMPTY: 'ApiErrPeersListEmpty',

  PAID_MEDIA_FORBIDDEN: 'ApiErrPaidMediaForbidden',

  USER_DISALLOWED_STARGIFTS: 'ApiErrUserDisallowedStargifts',
};

if (DEBUG) {
  READABLE_ERROR_MESSAGES.CHAT_WRITE_FORBIDDEN = 'ApiErrChatWriteForbidden';
  READABLE_ERROR_MESSAGES.CHAT_ADMIN_REQUIRED = 'ApiErrChatAdminRequired';
}

const SHIPPING_ERROR_KEYS: Record<string, { field: string; messageKey: RegularLangKey }> = {
  ADDRESS_STREET_LINE1_INVALID: { field: 'streetLine1', messageKey: 'PaymentErrorStreet' },
  ADDRESS_STREET_LINE2_INVALID: { field: 'streetLine2', messageKey: 'PaymentErrorStreet' },
  ADDRESS_CITY_INVALID: { field: 'city', messageKey: 'PaymentErrorCity' },
  ADDRESS_COUNTRY_INVALID: { field: 'countryIso2', messageKey: 'PaymentErrorCountry' },
  ADDRESS_POSTCODE_INVALID: { field: 'postCode', messageKey: 'PaymentErrorPostCode' },
  ADDRESS_STATE_INVALID: { field: 'state', messageKey: 'PaymentErrorState' },
  REQ_INFO_NAME_INVALID: { field: 'fullName', messageKey: 'PaymentErrorName' },
  REQ_INFO_PHONE_INVALID: { field: 'phone', messageKey: 'PaymentErrorPhone' },
  REQ_INFO_EMAIL_INVALID: { field: 'email', messageKey: 'PaymentErrorEmail' },
};

const FINAL_PAYMENT_ERRORS = new Set([
  'BOT_PRECHECKOUT_FAILED',
  'PAYMENT_FAILED',
]);

const ERROR_CODES_WITHOUT_DIALOG = new Set([
  406,
]);

export default function getReadableErrorText(error: ApiError) {
  const { message, isSlowMode, textParams } = error;
  // Currently, TheAdvance API doesn't return `SLOWMODE_WAIT_X` error as described in the docs
  if (isSlowMode) {
    const extraPartIndex = message.indexOf(' (caused by');
    return extraPartIndex > 0 ? message.substring(0, extraPartIndex) : message;
  }
  const errorKey = READABLE_ERROR_MESSAGES[message];
  if (!errorKey) return undefined;

  // Resolve without variables; server-provided `textParams` (if any) fill the placeholders below.
  let errorMessage = getTranslationFn().withRegular({ key: errorKey } as RegularLangFnParameters);
  if (textParams) {
    errorMessage = Object.keys(textParams).reduce((acc, current) => {
      return acc.replace(current, textParams[current]);
    }, errorMessage);
  }
  return errorMessage;
}

export function getShippingError(error: ApiError): ApiFieldError | undefined {
  const shippingError = SHIPPING_ERROR_KEYS[error.message];
  if (!shippingError) return undefined;

  return {
    field: shippingError.field,
    message: getTranslationFn()(shippingError.messageKey),
  };
}

export function shouldClosePaymentModal(error: ApiError): boolean {
  return FINAL_PAYMENT_ERRORS.has(error.message);
}

export function shouldShowErrorDialog(error: ApiError): boolean {
  if (error.code && ERROR_CODES_WITHOUT_DIALOG.has(error.code)) return false;
  if (error.hasErrorKey && !getReadableErrorText(error)) return false;

  return true;
}
